import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect } from "react"
import { Outlet, Route, Routes, useLocation } from "react-router-dom"
import { NavAndSideStyled } from "../styled/layout.styled"
import { WebsocketProvider } from "../websocket/WebsocketProvider"
import Nav from "./Nav"
import ProtectedRoute from "./ProtectedRoute"
import ScientGlobalStateProvider from "./ScientGlobalStateProvider"
import { useAuth } from "./hooks"
import ExcelFunctionsHelp from "./pages/Addin/ExcelFunctionsHelp"
import BloombergAuth from "./pages/BloombergAuth/BloombergAuth"
import ApiKeys from "./pages/Config/ApiKeys/ApiKeys"
import Company from "./pages/Config/Company/Company"
import Config from "./pages/Config/Config"
import Dataset from "./pages/Config/Dataset/Dataset"
import Instrument from "./pages/Config/Instrument/Instrument"
import Dataviz from "./pages/Dataviz"
import Fundamentals from "./pages/Fundamentals/FundamentalsDisplay"
import Home from "./pages/Home"
import { IdeaCacheProvider } from "./pages/Ideas/Contexts/IdeaCacheProvider"
import IdeaCreate from "./pages/Ideas/Create/IdeaCreate"
import Ideas from "./pages/Ideas/Ideas"
import Login from "./pages/Login/Login"
import Models from "./pages/Models/Models"
import NotFound from "./pages/NotFound"
import PasswordResetConfirm from "./pages/PasswordReset/PasswordResetConfirm"
import PasswordResetRequest from "./pages/PasswordReset/PasswordResetRequest"
import PortfolioManagement from "./pages/PortfolioManagement/PortfolioManagement"
import { TradingCacheProvider } from "./pages/PortfolioManagement/Trading/Contexts/TradingCacheProvider"
import Warren from "./pages/Warren"
import OAuthConsent from "./pages/OAuth/OAuthConsent"
import Reconciliation from "./pages/PortfolioManagement/Reconciliation/Reconciliation"

export const SCIENT_ROUTES = {
  ADMIN: "/admin",
  CONFIG: "/config",
  CONSENSUS: "/consensus",
  DATAVIZ: "/dataviz",
  FUNDAMENTALS: "/fundamentals",
  HOME: "/",
  IDEAS: "/ideas",
  LOGIN: "/login",
  PASSWORD_RESET_REQUEST: "/reset-password/request",
  LOGOUT: "/logout",
  MOCKUP: "/mockup",
  MODELS: "/models",
  PORTFOLIO_MANAGEMENT: "/portfolio-management",
  SESSION: "/session",
  PASSWORD_RESET_CONFIRM: "/reset-password/confirm/:token",
  ADDIN: "/addin",
  BLOOMBERG_AUTH: "/bbg",
  WARREN: "/warren",
  OAUTH_CONSENT: "/oauth/consent",
  RECONCILIATION: "/reconciliation",
}

const ScientRouter = () => {
  const queryClient = useQueryClient()
  const { profile } = useAuth()
  const location = useLocation()

  /**
   * On location change update Intercom
   */
  useEffect(() => {
    if (process.env.REACT_APP_INTERCOM === "true") {
      // @ts-ignore
      window.Intercom("update")
    }
  }, [location])

  /**
   * Invalidate queries associated to live events
   * when the websocket successfully reconnects
   * after being disconnected
   */
  const onRetrySuccessWs = useCallback(() => {
    queryClient.refetchQueries({
      predicate: query => {
        return (
          (query.queryKey[0] as string).includes("ideas") ||
          (query.queryKey[0] as string).includes("messages")
        )
      },
    })
  }, [queryClient])

  return (
    <Routes>
      <Route
        element={
          <ProtectedRoute>
            <WebsocketProvider onRetrySuccess={onRetrySuccessWs}>
              <ScientGlobalStateProvider>
                <IdeaCacheProvider>
                  <TradingCacheProvider>
                    <NavAndSideStyled>
                      <Nav />
                      <Outlet />
                    </NavAndSideStyled>
                  </TradingCacheProvider>
                </IdeaCacheProvider>
              </ScientGlobalStateProvider>
            </WebsocketProvider>
          </ProtectedRoute>
        }
      >
        <Route path={SCIENT_ROUTES.HOME} element={<Home />} />
        {!profile?.groups.includes("Restricted") && (
          <>
            <Route path={SCIENT_ROUTES.IDEAS} element={<Ideas />}>
              <Route path="create" element={<IdeaCreate />} />
              <Route path=":ideaId/update" element={<IdeaCreate />} />
              <Route path=":ideaId/revision/create" element={<IdeaCreate />} />
            </Route>
            <Route path={SCIENT_ROUTES.BLOOMBERG_AUTH} element={<BloombergAuth />} />
            <Route path={SCIENT_ROUTES.WARREN} element={<Warren />} />
          </>
        )}
        <Route path={SCIENT_ROUTES.PORTFOLIO_MANAGEMENT} element={<PortfolioManagement />} />
        <Route path={SCIENT_ROUTES.RECONCILIATION} element={<Reconciliation />} />
        <Route path={SCIENT_ROUTES.CONFIG} element={<Config />}>
          <Route path="companies" element={<Company />} />
          <Route path="datasets" element={<Dataset />} />
          <Route path="datasets/:datasetId" element={<Dataset />} />
          <Route path="instruments" element={<Instrument />} />
          <Route path="api-keys" element={<ApiKeys />} />
        </Route>
        <Route path={SCIENT_ROUTES.ADDIN} element={<ExcelFunctionsHelp />} />
        <Route path={SCIENT_ROUTES.DATAVIZ} element={<Dataviz />} />
        <Route path={SCIENT_ROUTES.FUNDAMENTALS} element={<Fundamentals />} />
        <Route path={SCIENT_ROUTES.MODELS} element={<Models />} />
      </Route>
      <Route path={SCIENT_ROUTES.LOGIN} element={<Login />} />
      <Route path={SCIENT_ROUTES.PASSWORD_RESET_REQUEST} element={<PasswordResetRequest />} />
      <Route path={SCIENT_ROUTES.PASSWORD_RESET_CONFIRM} element={<PasswordResetConfirm />} />
      <Route
        path={SCIENT_ROUTES.OAUTH_CONSENT}
        element={
          <ProtectedRoute>
            <OAuthConsent />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

export default ScientRouter
